import { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Calendar, Clock, Users, MapPin, Ticket, Loader2, CheckCircle, ArrowLeft,
  Mail, Phone, CreditCard, QrCode, Building, Wallet, Landmark, Copy, Download, User
} from 'lucide-react';
import { toast } from 'sonner';
import useStoreSubdomain from '@/hooks/useStoreSubdomain';

const statusLabels = { draft: 'Draft', upcoming: 'Akan Datang', ongoing: 'Sedang Berlangsung', completed: 'Selesai', cancelled: 'Dibatalkan' };
const statusColors = { upcoming: 'bg-blue-500', ongoing: 'bg-red-500', completed: 'bg-green-500', cancelled: 'bg-orange-500' };

function formatDate(iso) {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }); } catch { return iso; }
}
function formatDateTime(iso) {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return iso; }
}
function formatPrice(n) { return `Rp ${(n || 0).toLocaleString('id-ID')}`; }

export default function EventDetail() {
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const ticketParam = searchParams.get('ticket');
  const subdomain = useStoreSubdomain();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRegister, setShowRegister] = useState(false);
  const [selectedTicketType, setSelectedTicketType] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [form, setForm] = useState(() => {
    let init = { customer_name: '', customer_email: '', customer_phone: '', payment_method: 'qris' };
    const saved = localStorage.getItem('user');
    if (saved) { try { const u = JSON.parse(saved); if (u.role === 'customer') { init.customer_name = u.name || ''; init.customer_email = u.email || ''; } } catch {} }
    return init;
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [detailTab, setDetailTab] = useState('info');
  const [ticketInfo, setTicketInfo] = useState(null);

  // Autofill phone from customer profile
  useEffect(() => {
    const saved = localStorage.getItem('user');
    if (saved) { try { const u = JSON.parse(saved); if (u.role === 'customer' && localStorage.getItem('token')) { fetch('/api/customer/profile', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).then(r => r.ok ? r.json() : null).then(d => { if (d?.customer?.phone) setForm(f => ({ ...f, customer_phone: f.customer_phone || d.customer.phone })); }).catch(() => {}); } } catch {} }
  }, []);

  useEffect(() => {
    if (!subdomain || !eventId) return;
    setLoading(true);
    const fetches = [
      fetch(`/api/store/${subdomain}/events/${eventId}`).then(r => r.ok ? r.json() : Promise.reject()),
    ];
    if (ticketParam) {
      fetches.push(
        fetch(`/api/store/${subdomain}/events/${eventId}/ticket-info/${ticketParam}`)
          .then(r => r.ok ? r.json() : null)
          .catch(() => null)
      );
    }
    Promise.all(fetches)
      .then(([eventData, tInfo]) => {
        setEvent(eventData);
        if (tInfo) setTicketInfo(tInfo);
      })
      .catch(() => toast.error('Event tidak ditemukan'))
      .finally(() => setLoading(false));
  }, [subdomain, eventId, ticketParam]);

  const handleRegister = async () => {
    if (!form.customer_name || !form.customer_email) return toast.error('Nama dan email wajib diisi');
    if (!selectedTicketType) return toast.error('Pilih tipe tiket');
    setSubmitting(true);
    try {
      const res = await fetch(`/api/store/${subdomain}/events/${eventId}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticket_type_id: selectedTicketType.id,
          quantity,
          customer_name: form.customer_name,
          customer_email: form.customer_email,
          customer_phone: form.customer_phone,
          payment_method: form.payment_method,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Registrasi gagal');
      setResult(data);
      setShowRegister(false);
    } catch (e) { toast.error(e.message); }
    setSubmitting(false);
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="animate-spin mr-2" /> Memuat...</div>;
  if (!event) return <div className="flex flex-col items-center justify-center min-h-screen text-slate-500"><p>Event tidak ditemukan</p></div>;

  // Ticket download view (from notification link with ?ticket=)
  if (ticketInfo && !result) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-violet-50 to-white">
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-4">
              <Ticket size={32} className="text-violet-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">{event.title}</h1>
            {event.start_date && <p className="text-slate-500 mt-1">{formatDate(event.start_date)}</p>}
            {event.venue_name && <p className="text-slate-400 text-sm">{event.venue_name}</p>}
          </div>

          <Card className="mb-4">
            <CardContent className="p-6 space-y-3">
              <h3 className="font-bold text-slate-800">🎫 Tiket Anda</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-slate-500">Nomor Tiket:</span> <span className="font-mono font-bold">{ticketInfo.ticket_number}</span></div>
                <div><span className="text-slate-500">Nama:</span> <b>{ticketInfo.attendee_name}</b></div>
                <div><span className="text-slate-500">Tipe:</span> {ticketInfo.ticket_type}</div>
                <div><span className="text-slate-500">Status:</span>{' '}
                  <Badge className={ticketInfo.payment_status === 'paid' || ticketInfo.payment_status === 'free' ? 'bg-green-500 text-white' : 'bg-amber-500 text-white'}>
                    {ticketInfo.payment_status === 'free' ? 'Gratis' : ticketInfo.payment_status === 'paid' ? 'Lunas' : 'Menunggu Pembayaran'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {ticketInfo.can_download ? (
            <Card className="mb-4 border-green-200 bg-green-50">
              <CardContent className="p-6 text-center space-y-3">
                <CheckCircle size={32} className="text-green-600 mx-auto" />
                <p className="font-bold text-green-800">E-Ticket Anda Siap!</p>
                <a href={`/api/store/${subdomain}/events/${eventId}/tickets/${ticketInfo.ticket_id}/eticket`} target="_blank" rel="noreferrer">
                  <Button className="bg-green-600 hover:bg-green-700">
                    <Download size={16} className="mr-2" /> Download E-Ticket (PDF)
                  </Button>
                </a>
              </CardContent>
            </Card>
          ) : (
            <Card className="mb-4 border-amber-200 bg-amber-50">
              <CardContent className="p-6 text-center space-y-2">
                <p className="font-bold text-amber-800">⏳ Menunggu Pembayaran</p>
                <p className="text-sm text-amber-600">E-Ticket akan tersedia setelah pembayaran dikonfirmasi.</p>
              </CardContent>
            </Card>
          )}

          <div className="text-center mt-6">
            <Link to={subdomain ? '/' : `/store/${subdomain}`}><Button variant="outline"><ArrowLeft size={14} className="mr-1" /> Kembali ke Toko</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  // After registration result
  if (result) {
    const isPaid = !!(result.order || result.payment_url || result.bank_accounts);
    const isBankTransfer = result.bank_accounts && result.bank_accounts.length > 0;
    const orderNum = result.order?.order_number || result.registration?.order_number || '';
    return (
      <div className="min-h-screen bg-gradient-to-b from-violet-50 to-white">
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">{isPaid ? 'Pesanan Dibuat!' : 'Registrasi Berhasil!'}</h1>
            <p className="text-slate-500 mt-2">{isPaid ? 'Silakan lakukan pembayaran untuk mendapatkan tiket Anda' : 'Tiket Anda sudah siap!'}</p>
          </div>

          <Card className="mb-4">
            <CardContent className="p-6 space-y-3">
              <h3 className="font-bold text-slate-800">🎫 Detail Event</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-slate-500">Event:</span> <b>{event.title}</b></div>
                <div><span className="text-slate-500">Tiket:</span> {selectedTicketType?.name}</div>
                <div><span className="text-slate-500">Jumlah:</span> {quantity}</div>
                <div><span className="text-slate-500">Total:</span> <b>{selectedTicketType?.price > 0 ? formatPrice(selectedTicketType.price * quantity) : 'GRATIS'}</b></div>
                {orderNum && <div className="col-span-2"><span className="text-slate-500">No. Order:</span> <span className="font-mono font-bold">{orderNum}</span></div>}
                {event.venue_name && <div className="col-span-2"><span className="text-slate-500">Venue:</span> {event.venue_name}</div>}
                {event.start_date && <div className="col-span-2"><span className="text-slate-500">Tanggal:</span> {formatDate(event.start_date)}</div>}
              </div>
            </CardContent>
          </Card>

          <Card className="mb-4">
            <CardContent className="p-6 space-y-3">
              <h3 className="font-bold text-slate-800">👤 Data Peserta</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-slate-500">Nama:</span> {form.customer_name}</div>
                <div><span className="text-slate-500">Email:</span> {form.customer_email}</div>
                {form.customer_phone && <div><span className="text-slate-500">Telepon:</span> {form.customer_phone}</div>}
              </div>
            </CardContent>
          </Card>

          {isPaid && isBankTransfer && (
            <Card className="mb-4 border-blue-200 bg-blue-50">
              <CardContent className="p-6 space-y-3">
                <h3 className="font-bold text-blue-800">🏦 Transfer Pembayaran</h3>
                <div className="text-sm text-blue-700 mb-2">
                  <div>Total: <b>{formatPrice(selectedTicketType.price * quantity)}</b></div>
                </div>
                <div className="space-y-2">
                  {result.bank_accounts.map((acc, i) => (
                    <div key={i} className="bg-white rounded-lg p-3 border border-blue-200">
                      <div className="font-bold text-slate-800">{acc.bank_name}</div>
                      <div className="font-mono text-lg font-bold text-blue-600">{acc.account_number}</div>
                      <div className="text-sm text-slate-500">a.n. {acc.account_holder}</div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-blue-600 mt-2">⚠️ Pastikan transfer sesuai nominal. Setelah transfer, konfirmasi pembayaran melalui WhatsApp atau halaman event.</p>
              </CardContent>
            </Card>
          )}

          {isPaid && !isBankTransfer && result.payment_url && (
            <Card className="mb-4 border-amber-200 bg-amber-50">
              <CardContent className="p-6 space-y-3">
                <h3 className="font-bold text-amber-800">💳 Pembayaran</h3>
                <div className="text-sm space-y-1">
                  {orderNum && <div><span className="text-amber-700">No. Order:</span> <span className="font-mono">{orderNum}</span></div>}
                  <div><span className="text-amber-700">Metode:</span> {form.payment_method?.toUpperCase()}</div>
                  <div><span className="text-amber-700">Total:</span> <b>{formatPrice(selectedTicketType.price * quantity)}</b></div>
                </div>
                <a href={result.payment_url} target="_blank" rel="noreferrer">
                  <Button className="w-full bg-amber-600 hover:bg-amber-700">
                    <CreditCard size={16} className="mr-2" /> Bayar Sekarang
                  </Button>
                </a>
              </CardContent>
            </Card>
          )}

          {!isPaid && result.tickets?.length > 0 && (
            <Card className="mb-4 border-green-200 bg-green-50">
              <CardContent className="p-6 space-y-3">
                <h3 className="font-bold text-green-800">🎟️ Tiket Anda</h3>
                {result.tickets.map((t, i) => (
                  <div key={i} className="flex items-center justify-between bg-white rounded-lg p-3 border">
                    <div>
                      <p className="font-mono text-sm font-bold">{t.ticket_number}</p>
                      <p className="text-xs text-slate-500">{t.attendee_name} — {selectedTicketType?.name}</p>
                    </div>
                    <a href={`/api/store/${subdomain}/events/${eventId}/tickets/${t.id}/eticket`} target="_blank" rel="noreferrer">
                      <Button size="sm" variant="outline"><Download size={14} className="mr-1" /> E-Ticket</Button>
                    </a>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="p-6">
              <h3 className="font-bold text-slate-800 mb-3">📋 Selanjutnya</h3>
              <ol className="space-y-2 text-sm text-slate-600 list-decimal list-inside">
                {isPaid && isBankTransfer ? (
                  <>
                    <li>Transfer ke salah satu rekening di atas sesuai nominal</li>
                    <li>Konfirmasi pembayaran via WhatsApp atau halaman event</li>
                    <li>Tiket akan dikirim via email & WhatsApp setelah pembayaran dikonfirmasi</li>
                    <li>Tunjukkan E-Ticket / QR Code saat check-in di lokasi</li>
                  </>
                ) : isPaid ? (
                  <>
                    <li>Selesaikan pembayaran melalui link di atas</li>
                    <li>Tiket akan dikirim via email & WhatsApp setelah pembayaran dikonfirmasi</li>
                    <li>Tunjukkan E-Ticket / QR Code saat check-in di lokasi</li>
                  </>
                ) : (
                  <>
                    <li>Download E-Ticket dari link di atas atau cek email Anda</li>
                    <li>Simpan E-Ticket dan tampiland di HP saat check-in</li>
                    <li>Datang tepat waktu di lokasi event</li>
                  </>
                )}
              </ol>
            </CardContent>
          </Card>

          <div className="text-center mt-6">
            <Link to={`/store/${subdomain}`}><Button variant="outline"><ArrowLeft size={14} className="mr-1" /> Kembali ke Toko</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  // Main Event Detail Page
  const ticketTypes = event.ticket_types || [];
  const schedules = event.schedules || [];
  const venueSections = event.venue_sections || [];
  const minPrice = ticketTypes.length ? Math.min(...ticketTypes.map(t => t.price)) : 0;
  const maxPrice = ticketTypes.length ? Math.max(...ticketTypes.map(t => t.price)) : 0;
  const totalSold = ticketTypes.reduce((s, t) => s + (t.sold_count || 0), 0);
  const totalCapacity = ticketTypes.reduce((s, t) => s + (t.quota || 0), 0);

  const openRegister = (tt) => {
    setSelectedTicketType(tt);
    setQuantity(1);
    setForm({ customer_name: '', customer_email: '', customer_phone: '', payment_method: 'qris' });
    setShowRegister(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 to-white">
      {/* Hero */}
      <div className="relative bg-gradient-to-r from-violet-600 to-indigo-700 text-white">
        <div className="max-w-5xl mx-auto px-4 py-12 md:py-16">
          <Link to={`/store/${subdomain}`} className="inline-flex items-center gap-1 text-white/70 hover:text-white text-sm mb-4">
            <ArrowLeft size={14} /> Kembali
          </Link>
          <div className="flex flex-col md:flex-row gap-8">
            <div className="flex-1 space-y-4">
              <div className="flex items-center gap-2">
                <Badge className={`${statusColors[event.status] || 'bg-gray-500'} text-white`}>{statusLabels[event.status] || event.status}</Badge>
                {event.category && <Badge className="bg-white/20">{event.category}</Badge>}
              </div>
              <h1 className="text-3xl md:text-4xl font-extrabold">{event.title}</h1>
              {event.description && <p className="text-white/80 text-sm md:text-base line-clamp-3">{event.description}</p>}
              <div className="flex flex-wrap gap-4 text-sm text-white/70">
                {event.start_date && <span className="flex items-center gap-1"><Calendar size={14} /> {formatDate(event.start_date)}</span>}
                {event.venue_name && <span className="flex items-center gap-1"><MapPin size={14} /> {event.venue_name}{event.venue_city ? `, ${event.venue_city}` : ''}</span>}
                <span className="flex items-center gap-1"><Users size={14} /> {totalSold} peserta</span>
              </div>
            </div>
            {event.thumbnail && (
              <div className="w-full md:w-64 flex-shrink-0">
                <img src={event.thumbnail} alt="" className="w-full h-48 md:h-full object-cover rounded-lg shadow-lg" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Left */}
          <div className="flex-1 space-y-6">
            <Tabs value={detailTab} onValueChange={setDetailTab}>
              <TabsList>
                <TabsTrigger value="info">Detail</TabsTrigger>
                {schedules.length > 0 && <TabsTrigger value="schedule">Jadwal</TabsTrigger>}
                {venueSections.length > 0 && <TabsTrigger value="venue">Venue</TabsTrigger>}
              </TabsList>

              <TabsContent value="info" className="space-y-4 pt-4">
                {event.description && (
                  <Card><CardContent className="p-6">
                    <h3 className="font-bold text-slate-800 mb-3">Tentang Event</h3>
                    <p className="text-sm text-slate-600 whitespace-pre-line">{event.description}</p>
                  </CardContent></Card>
                )}
                <Card><CardContent className="p-6 space-y-3">
                  <h3 className="font-bold text-slate-800 mb-3">📍 Lokasi</h3>
                  <div className="text-sm text-slate-600 space-y-1">
                    {event.venue_name && <p className="font-medium">{event.venue_name}</p>}
                    {event.venue_address && <p>{event.venue_address}</p>}
                    {event.venue_city && <p>{event.venue_city}</p>}
                    {event.venue_map_url && (
                      <a href={event.venue_map_url} target="_blank" rel="noreferrer" className="text-violet-600 underline text-sm flex items-center gap-1">
                        <MapPin size={14} /> Buka di Google Maps
                      </a>
                    )}
                  </div>
                </CardContent></Card>
                {event.organizer_name && (
                  <Card><CardContent className="p-6 space-y-2">
                    <h3 className="font-bold text-slate-800">👤 Organizer</h3>
                    <div className="text-sm text-slate-600">
                      <p className="font-medium">{event.organizer_name}</p>
                      {event.organizer_phone && <p className="flex items-center gap-1"><Phone size={12} /> {event.organizer_phone}</p>}
                      {event.organizer_email && <p className="flex items-center gap-1"><Mail size={12} /> {event.organizer_email}</p>}
                    </div>
                  </CardContent></Card>
                )}
              </TabsContent>

              {schedules.length > 0 && (
                <TabsContent value="schedule" className="space-y-4 pt-4">
                  {schedules.map(sch => (
                    <Card key={sch.id}>
                      <CardContent className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <Calendar size={18} className="text-violet-500" />
                          <div>
                            <h3 className="font-bold text-slate-800">{sch.day_label}</h3>
                            {sch.date && <p className="text-xs text-slate-400">{formatDate(sch.date)}</p>}
                          </div>
                        </div>
                        <div className="relative pl-6 border-l-2 border-violet-200 space-y-4">
                          {(sch.sessions || []).map((s, i) => (
                            <div key={i} className="relative">
                              <div className="absolute -left-[25px] w-3 h-3 rounded-full bg-violet-500 border-2 border-white" />
                              <div className="bg-slate-50 rounded-lg p-3">
                                <div className="flex items-center gap-2 text-sm">
                                  <Clock size={12} className="text-violet-500" />
                                  <span className="font-mono text-slate-500">{s.time}{s.end_time ? ` - ${s.end_time}` : ''}</span>
                                </div>
                                <p className="font-semibold text-slate-800 mt-1">{s.title}</p>
                                {s.speaker_name && <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1"><User size={10} /> {s.speaker_name}</p>}
                                {s.location && <p className="text-xs text-slate-400 flex items-center gap-1"><MapPin size={10} /> {s.location}</p>}
                                {s.description && <p className="text-xs text-slate-500 mt-1">{s.description}</p>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </TabsContent>
              )}

              {venueSections.length > 0 && (
                <TabsContent value="venue" className="space-y-4 pt-4">
                  <Card><CardContent className="p-6">
                    <h3 className="font-bold text-slate-800 mb-4">🏛️ Layout Venue</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {venueSections.map(vs => (
                        <div key={vs.id} className="rounded-lg border-2 p-4 text-center" style={{ borderColor: vs.color, backgroundColor: vs.color + '10' }}>
                          <div className="w-8 h-8 rounded-full mx-auto mb-2" style={{ backgroundColor: vs.color }} />
                          <p className="font-bold text-sm">{vs.name}</p>
                          <p className="text-xs text-slate-500">{vs.capacity ? `${vs.capacity} kursi` : 'Unlimited'}</p>
                          {vs.description && <p className="text-xs text-slate-400 mt-1">{vs.description}</p>}
                        </div>
                      ))}
                    </div>
                    {event.venue_map_url && (
                      <a href={event.venue_map_url} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1 text-violet-600 underline text-sm">
                        <MapPin size={14} /> Lihat Peta Lokasi
                      </a>
                    )}
                  </CardContent></Card>
                </TabsContent>
              )}
            </Tabs>
          </div>

          {/* Right Sidebar — Ticket Types */}
          <div className="w-full md:w-80 space-y-4">
            <Card className="sticky top-4">
              <CardContent className="p-6 space-y-4">
                <h3 className="font-bold text-slate-800 flex items-center gap-2"><Ticket size={18} /> Pilih Tiket</h3>
                {ticketTypes.length === 0 ? (
                  <p className="text-sm text-slate-400">Tiket belum tersedia</p>
                ) : (
                  <div className="space-y-3">
                    {ticketTypes.filter(tt => tt.is_active).map(tt => {
                      const remaining = tt.quota ? tt.quota - (tt.sold_count || 0) : null;
                      const soldOut = remaining !== null && remaining <= 0;
                      return (
                        <div key={tt.id} className="border rounded-lg p-3 space-y-2" style={{ borderLeftWidth: 4, borderLeftColor: tt.color }}>
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-sm">{tt.name}</span>
                            {soldOut && <Badge className="bg-red-100 text-red-600">Habis</Badge>}
                          </div>
                          <p className="text-lg font-extrabold text-violet-700">{tt.price > 0 ? formatPrice(tt.price) : 'GRATIS'}</p>
                          {tt.description && <p className="text-xs text-slate-500">{tt.description}</p>}
                          {(tt.benefits || []).length > 0 && (
                            <ul className="text-xs text-slate-500 space-y-0.5">{tt.benefits.map((b, i) => <li key={i} className="flex items-center gap-1"><CheckCircle size={10} className="text-green-500" /> {b}</li>)}</ul>
                          )}
                          {tt.quota > 0 && (
                            <div>
                              <div className="flex justify-between text-[10px] text-slate-400 mb-1">
                                <span>{tt.sold_count || 0} terjual</span>
                                <span>{remaining} tersisa</span>
                              </div>
                              <div className="w-full bg-slate-100 rounded-full h-1.5">
                                <div className="bg-violet-500 h-1.5 rounded-full transition-all" style={{ width: `${Math.min(100, ((tt.sold_count || 0) / tt.quota) * 100)}%` }} />
                              </div>
                            </div>
                          )}
                          {tt.section_name && <p className="text-[10px] text-slate-400">Section: {tt.section_name}</p>}
                          <Button className="w-full" size="sm" disabled={soldOut} onClick={() => openRegister(tt)}>
                            {soldOut ? 'Sold Out' : tt.price > 0 ? 'Beli Tiket' : 'Daftar Gratis'}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* Event info */}
                <div className="border-t pt-3 space-y-2 text-sm text-slate-500">
                  {event.start_date && <div className="flex items-center gap-2"><Calendar size={14} /> {formatDate(event.start_date)}</div>}
                  {event.end_date && <div className="flex items-center gap-2"><Clock size={14} /> s/d {formatDate(event.end_date)}</div>}
                  {event.venue_name && <div className="flex items-center gap-2"><MapPin size={14} /> {event.venue_name}</div>}
                  {totalCapacity > 0 && <div className="flex items-center gap-2"><Users size={14} /> {totalSold}/{totalCapacity} peserta</div>}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Register Dialog */}
      <Dialog open={showRegister} onOpenChange={setShowRegister}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Daftar — {selectedTicketType?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-50 rounded-lg p-3 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Tiket:</span><span className="font-bold">{selectedTicketType?.name}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Harga:</span><span>{selectedTicketType?.price > 0 ? formatPrice(selectedTicketType.price) : 'GRATIS'}</span></div>
            </div>
            <div>
              <Label>Jumlah Tiket</Label>
              <div className="flex items-center gap-2 mt-1">
                <Button size="sm" variant="outline" onClick={() => setQuantity(q => Math.max(1, q - 1))} disabled={quantity <= 1}>-</Button>
                <span className="w-12 text-center font-bold">{quantity}</span>
                <Button size="sm" variant="outline" onClick={() => setQuantity(q => Math.min(10, q + 1))} disabled={quantity >= 10}>+</Button>
                {selectedTicketType?.price > 0 && <span className="text-sm text-slate-500 ml-2">= {formatPrice(selectedTicketType.price * quantity)}</span>}
              </div>
            </div>
            <div><Label>Nama Lengkap *</Label><Input value={form.customer_name} onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))} /></div>
            <div><Label>Email *</Label><Input type="email" value={form.customer_email} onChange={e => setForm(f => ({ ...f, customer_email: e.target.value }))} /></div>
            <div><Label>No. WhatsApp</Label><Input value={form.customer_phone} onChange={e => setForm(f => ({ ...f, customer_phone: e.target.value }))} placeholder="08xxxx" /></div>
            {selectedTicketType?.price > 0 && (
              <div>
                <Label>Metode Pembayaran</Label>
                <RadioGroup value={form.payment_method} onValueChange={v => setForm(f => ({ ...f, payment_method: v }))} className="mt-2 grid grid-cols-2 gap-2">
                  {[
                    { value: 'qris', icon: QrCode, label: 'QRIS' },
                    { value: 'va', icon: Building, label: 'Virtual Account' },
                    { value: 'ewallet', icon: Wallet, label: 'E-Wallet' },
                    { value: 'bank_transfer', icon: Landmark, label: 'Bank Transfer' },
                  ].map(m => (
                    <Label key={m.value} className={`flex items-center gap-2 border rounded-lg p-3 cursor-pointer transition-colors ${form.payment_method === m.value ? 'border-violet-500 bg-violet-50' : 'hover:bg-slate-50'}`}>
                      <RadioGroupItem value={m.value} />
                      <m.icon size={14} /> <span className="text-sm">{m.label}</span>
                    </Label>
                  ))}
                </RadioGroup>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRegister(false)}>Batal</Button>
            <Button onClick={handleRegister} disabled={submitting}>
              {submitting && <Loader2 size={14} className="animate-spin mr-1" />}
              {selectedTicketType?.price > 0 ? `Bayar ${formatPrice(selectedTicketType.price * quantity)}` : 'Daftar Sekarang'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
